import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {ListLoading} from '@github-ui/list-view-items-issues-prs/ListLoading'
import {getQuery} from '@github-ui/list-view-items-issues-prs/Query'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation} from '@github-ui/react-router'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {type MutableRefObject, Suspense} from 'react'
import {graphql, useFragment, type UseQueryLoaderLoadQueryOptions} from 'react-relay'

import {VALUES} from '../../constants/values'
import type {IssueIndexPageQuery$variables} from '../../pages/__generated__/IssueIndexPageQuery.graphql'
import type {AppPayload} from '../../types/app-payload'
import type {IssueSearchType} from '../../types/issue-search-type'
import {ListError, SsoListError} from '../list/ListError'
import {ListItems} from '../list/ListItems'
import type {SearchList$key} from './__generated__/SearchList.graphql'
import type {SearchListRepo$key} from './__generated__/SearchListRepo.graphql'

type SearchListProps = {
  itemIdentifier: ItemIdentifier | undefined
  query: string
  search: SearchList$key
  repository: SearchListRepo$key | null
  loadSearchQuery?: (vars: IssueIndexPageQuery$variables, opts: UseQueryLoaderLoadQueryOptions) => void
  queryFromCustomView?: string | null
  listRef: MutableRefObject<HTMLUListElement | undefined>
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  onIssueNavigate?: (position?: number) => void
  updateIssueSearchType?: (issueSearchType: string | null | undefined) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
  searchType?: IssueSearchType
  hasSsoOrgs?: boolean
}

export function SearchList({
  itemIdentifier,
  query,
  search,
  repository,
  loadSearchQuery,
  queryFromCustomView,
  listRef,
  onSidePanelNavigate,
  onIssueNavigate,
  updateIssueSearchType,
  onListItemsChange,
  searchType,
  hasSsoOrgs,
}: SearchListProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchList on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
        type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
      ) {
        ...ListItemsPaginated_results
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            skip: $skip
            fetchRepository: $fetchRepository
            type: $type
          )
      }
    `,
    search,
  )

  const {scoped_repository, best_match_sort_enabled} = useAppPayload<AppPayload>()
  const {pathname} = useLocation()
  const showBorder = !(itemIdentifier?.number !== undefined || pathname === '/issues/new')

  const repo = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchListRepo on Repository {
        viewerCanPush
        isDisabled
        isLocked
        isArchived
        isInOrganization
      }
    `,
    repository,
  )

  const isBulkSupported = repo && repo.viewerCanPush && !(repo.isDisabled || repo.isLocked || repo.isArchived)

  const suspendedFilterLink = [
    <ListViewSectionFilterLink key="open" title="Open" isLoading href={''} />,
    <ListViewSectionFilterLink key="closed" title="Closed" isLoading href={''} />,
  ]

  return (
    <Suspense
      fallback={
        <ListLoading
          sectionFilters={suspendedFilterLink}
          showBorder={showBorder}
          layoutDensity={'default'}
          pageSize={VALUES.issuesPageSize()}
        />
      }
    >
      <PreloadedQueryBoundary
        critical
        fallback={hasSsoOrgs ? SsoListError : ListError}
        onRetry={() => {
          if (!query || !loadSearchQuery) return
          loadSearchQuery(
            {
              query: getQuery(query, scoped_repository, best_match_sort_enabled),
              owner: scoped_repository ? scoped_repository.owner : '',
              name: scoped_repository ? scoped_repository.name : '',
              ...(searchType && searchType !== 'ISSUE_ADVANCED' && {type: searchType}),
            },
            {fetchPolicy: 'network-only'},
          )
        }}
      >
        <RelayCachePreheatContextProvider>
          <ListItems
            search={data}
            queryFromCustomView={queryFromCustomView}
            listRef={listRef}
            isBulkSupported={isBulkSupported ?? false}
            onSidePanelNavigate={onSidePanelNavigate}
            isInOrganization={repo?.isInOrganization ?? false}
            onIssueNavigate={onIssueNavigate}
            updateIssueSearchType={updateIssueSearchType}
            onListItemsChange={onListItemsChange}
            searchType={searchType}
          />
        </RelayCachePreheatContextProvider>
      </PreloadedQueryBoundary>
    </Suspense>
  )
}
